import { getAuthToken, logout } from './auth';
import { buildApiUrl } from './apiUrl';

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  status: number;
}

// Generic API call function with JWT expiration handling
export const apiCall = async <T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  try {
    const token = getAuthToken();
    
    // If no token, user should be logged out already
    if (!token) {
      return { error: 'No authentication token', status: 401 };
    }

    const response = await fetch(buildApiUrl(endpoint), {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    // Handle 401 Unauthorized (token expired or invalid)
    if (response.status === 401) {
      logout();
      return { error: 'Authentication expired. Please login again.', status: 401 };
    }

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Request failed', status: response.status };
    }

    return { data, status: response.status };
  } catch (error: any) {
    return { error: error.message || 'Network error', status: 500 };
  }
};

// Specific API functions for common operations
export const apiGet = <T = any>(endpoint: string): Promise<ApiResponse<T>> => {
  return apiCall<T>(endpoint, { method: 'GET' });
};

export const apiPost = <T = any>(endpoint: string, body: any): Promise<ApiResponse<T>> => {
  return apiCall<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const apiPut = <T = any>(endpoint: string, body: any): Promise<ApiResponse<T>> => {
  return apiCall<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const apiDelete = <T = any>(endpoint: string, body?: any): Promise<ApiResponse<T>> => {
  return apiCall<T>(endpoint, {
    method: 'DELETE',
    body: body ? JSON.stringify(body) : undefined,
  });
};

// File upload API call
export const apiUpload = async <T = any>(
  endpoint: string,
  formData: FormData
): Promise<ApiResponse<T>> => {
  try {
    const token = getAuthToken();
    
    if (!token) {
      return { error: 'No authentication token', status: 401 };
    }

    const response = await fetch(buildApiUrl(endpoint), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (response.status === 401) {
      logout();
      return { error: 'Authentication expired. Please login again.', status: 401 };
    }

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Upload failed', status: response.status };
    }

    return { data, status: response.status };
  } catch (error: any) {
    return { error: error.message || 'Upload failed', status: 500 };
  }
}; 
